import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search,
  Bell,
  LogOut,
  ChevronDown,
  Package,
  ShoppingCart,
  CreditCard,
  Users,
  Settings,
  AlertTriangle,
  Star,
  XCircle,
  DollarSign,
  BellRing,
  BellOff,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getInitials } from '../../lib/utils';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { NotificationService, type NotificationData } from '../../services/notificationService';

interface HeaderProps {
  title: string;
  subtitle?: string;
  sidebarCollapsed: boolean;
}

export function Header({ title, subtitle, sidebarCollapsed }: HeaderProps) {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toastNotificationsEnabled, setToastNotificationsEnabled] = useState(true);
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const previousNotificationsRef = useRef<NotificationData[]>([]);

  // Load toast notification preference from localStorage on mount
  useEffect(() => {
    const savedPreference = localStorage.getItem('admin-toast-notifications-enabled');
    if (savedPreference !== null) {
      setToastNotificationsEnabled(JSON.parse(savedPreference));
    }
  }, []);

  // Save toast notification preference to localStorage when changed
  const handleToastNotificationToggle = (enabled: boolean) => {
    setToastNotificationsEnabled(enabled);
    localStorage.setItem('admin-toast-notifications-enabled', JSON.stringify(enabled));
    
    // Show a confirmation toast
    if (enabled) {
      toast.success('Toast notifications enabled', {
        icon: <BellRing className="w-4 h-4" />,
        duration: 3000,
      });
    } else {
      toast.info('Toast notifications disabled', {
        icon: <BellOff className="w-4 h-4" />,
        duration: 3000,
      });
    }
  };

  // Real-time notifications listener
  useEffect(() => {
    const unsubscribeNotifications = NotificationService.getNotificationsListener((newNotifications) => {
      const previousNotifications = previousNotificationsRef.current;
      
      // Check for new notifications by comparing with previous state
      if (previousNotifications.length > 0 && toastNotificationsEnabled) {
        const newNotificationIds = new Set(newNotifications.map(n => n.id));
        const previousNotificationIds = new Set(previousNotifications.map(n => n.id));
        
        // Find truly new notifications (not just updates)
        const freshNotifications = newNotifications.filter(notification => 
          !previousNotificationIds.has(notification.id) && !notification.read
        );
        
        // Show toast for each new notification only if enabled
        freshNotifications.forEach(notification => {
          showNotificationToast(notification);
        });
      }
      
      // Update state and ref
      setNotifications(newNotifications);
      previousNotificationsRef.current = newNotifications;
    }, 20); // Get latest 20 notifications

    const unsubscribeUnreadCount = NotificationService.getUnreadCountListener((count) => {
      setUnreadCount(count);
    });

    return () => {
      unsubscribeNotifications();
      unsubscribeUnreadCount();
    };
  }, [toastNotificationsEnabled]);

  // Helper function to get notification icon component
  const getNotificationIconComponent = (type: string) => {
    const iconMap = {
      ShoppingCart,
      AlertTriangle,
      CreditCard,
      XCircle,
      Users,
      Star,
      Bell,
      Package,
      Settings,
      DollarSign,
    };

    const { icon: iconName } = NotificationService.getNotificationIcon(type);
    const IconComponent = iconMap[iconName as keyof typeof iconMap] || Bell;
    
    return IconComponent;
  };

  // Handle notification click
  const handleNotificationClick = async (notification: NotificationData) => {
    try {
      // Mark as read if unread
      if (!notification.read) {
        await NotificationService.markAsRead(notification.id);
      }

      // Navigate to relevant page
      const actionUrl = NotificationService.getActionUrl(notification);
      navigate(actionUrl);
      setShowNotifications(false);
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  // Function to show toast notification (only called if toastNotificationsEnabled is true)
  const showNotificationToast = (notification: NotificationData) => {
    const IconComponent = getNotificationIconComponent(notification.type);
    
    // Get priority emoji based on priority level
    const getPriorityEmoji = (priority: string) => {
      switch (priority) {
        case 'urgent':
          return '🚨';
        case 'high':
          return '🔴';
        case 'medium':
          return '🟡';
        default:
          return '';
      }
    };

    const priorityEmoji = getPriorityEmoji(notification.priority);
    const title = `${priorityEmoji} ${notification.title}`.trim();

    toast(title, {
      description: notification.message,
      icon: <IconComponent className="w-4 h-4" />,
      duration: notification.priority === 'urgent' ? 10000 : 
               notification.priority === 'high' ? 8000 : 6000,
      action: {
        label: 'View',
        onClick: () => handleNotificationClick(notification),
      },
      className: 'cursor-pointer',
      style: {
        borderLeft: `4px solid ${
          notification.priority === 'urgent' ? '#dc2626' :
          notification.priority === 'high' ? '#ea580c' :
          notification.priority === 'medium' ? '#d97706' : '#6b7280'
        }`,
      },
      onDismiss: () => {
        // Mark as read when dismissed
        if (!notification.read) {
          NotificationService.markAsRead(notification.id).catch(console.error);
        }
      },
    });
  };

  // Mark all as read with loading state
  const handleMarkAllAsRead = async () => {
    if (isMarkingAllAsRead) return; // Prevent multiple clicks
    
    try {
      setIsMarkingAllAsRead(true);
      await NotificationService.markAllAsRead();
      toast.success('All notifications marked as read', {
        duration: 3000,
      });
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read. Please try again.');
    } finally {
      setIsMarkingAllAsRead(false);
    }
  };

  // Dynamic height measurement using ResizeObserver
  useEffect(() => {
    const measureHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        // Dispatch event for AdminLayout component
        window.dispatchEvent(new CustomEvent('headerHeightUpdate', { 
          detail: { height } 
        }));
      }
    };

    // Initial measurement with delay to ensure DOM is updated
    const timeoutId = setTimeout(measureHeaderHeight, 50);

    // Set up ResizeObserver to watch for header changes
    const resizeObserver = new ResizeObserver(() => {
      // Add small delay to ensure transitions complete
      setTimeout(measureHeaderHeight, 50);
    });
    
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [title, subtitle, sidebarCollapsed]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header 
      ref={headerRef}
      className="bg-white border-b border-gray-200 shadow-sm fixed top-0 right-0 z-[10000] transition-all duration-300 ease-in-out"
      style={{ left: sidebarCollapsed ? '80px' : '280px' }}
    >
      <div className="flex items-center justify-between px-6 py-4">
        {/* Title Section */}
        <div className="flex-1">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            {subtitle && (
              <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
            )}
          </motion.div>
        </div>

        {/* Search Bar */}
        <div className="flex-1 max-w-md mx-8">
          <motion.div 
            className="relative group"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-hover:text-jamaica-green" />
            <Input
              type="text"
              placeholder="Search anything..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-0 rounded-xl focus:bg-white focus:shadow-lg focus:ring-2 focus:ring-jamaica-green/20 transition-all duration-300 placeholder:text-gray-400 text-gray-700 font-medium"
            />
            {searchQuery && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 transition-colors"
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            )}
          </motion.div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-600 hover:text-jamaica-green hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </motion.span>
              )}
            </motion.button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-[10002] max-h-96 overflow-hidden"
                onBlur={() => setShowNotifications(false)}
              >
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-100">
                  {/* Top row: Title and action buttons */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                    <div className="flex items-center space-x-2">
                      {unreadCount > 0 && (
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                          {unreadCount} new
                        </span>
                      )}
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllAsRead}
                          disabled={isMarkingAllAsRead}
                          className="flex items-center space-x-1 text-xs text-jamaica-green hover:text-jamaica-green/80 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isMarkingAllAsRead ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span>Marking...</span>
                            </>
                          ) : (
                            <span>Mark all read</span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Bottom row: Toast notification toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {toastNotificationsEnabled ? (
                        <BellRing className="w-4 h-4 text-jamaica-green" />
                      ) : (
                        <BellOff className="w-4 h-4 text-gray-400" />
                      )}
                      <span className="text-sm text-gray-700">
                        Toast notifications
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs font-medium ${toastNotificationsEnabled ? 'text-jamaica-green' : 'text-gray-400'}`}>
                        {toastNotificationsEnabled ? 'ON' : 'OFF'}
                      </span>
                      <Switch
                        checked={toastNotificationsEnabled}
                        onCheckedChange={handleToastNotificationToggle}
                        className="data-[state=checked]:bg-jamaica-green"
                      />
                    </div>
                  </div>
                </div>

                {/* Notifications List */}
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((notification, index) => {
                      const IconComponent = getNotificationIconComponent(notification.type);
                      const { color } = NotificationService.getNotificationIcon(notification.type);
                      const formattedTime = NotificationService.formatTime(notification.createdAt);
                      
                      return (
                        <motion.div
                          key={notification.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          onClick={() => handleNotificationClick(notification)}
                          className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${
                            !notification.read ? 'bg-blue-50/50' : ''
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${color}`}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className={`text-sm font-medium ${
                                  !notification.read ? 'text-gray-900' : 'text-gray-700'
                                }`}>
                                  {notification.title}
                                </p>
                                {!notification.read && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{notification.message}</p>
                              <div className="flex items-center justify-between mt-1">
                                <p className="text-xs text-gray-500">{formattedTime}</p>
                                {notification.priority !== 'low' && (
                                  <span className={`text-xs font-medium ${NotificationService.getPriorityColor(notification.priority)}`}>
                                    {notification.priority.toUpperCase()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                  <div className="px-4 py-3 border-t border-gray-100">
                    <button 
                      onClick={() => {
                        navigate('/notifications');
                        setShowNotifications(false);
                      }}
                      className="w-full text-center text-sm text-jamaica-green hover:text-jamaica-green/80 font-medium"
                    >
                      View All Notifications
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-full flex items-center justify-center text-white text-sm font-medium">
                {userProfile ? getInitials(userProfile.name) : 'U'}
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm font-medium text-gray-900">
                  {userProfile?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {userProfile?.role?.replace('_', ' ') || 'Admin'}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </motion.button>

            {/* User Dropdown Menu */}
            {showUserMenu && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[10002]"
                onBlur={() => setShowUserMenu(false)}
              >
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">
                    {userProfile?.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {userProfile?.email}
                  </p>
                </div>
                
                <div className="pt-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign Out
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
} 